import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { CheckCircle2, Loader2, AlertTriangle, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useEntitlement } from "@/hooks/useEntitlement";
import { trackAppEvent } from "@/lib/app-analytics";
import { clearCheckoutReturnTo, readCheckoutReturnTo } from "@/lib/checkout-return-to";
import { confirmCheckoutSession } from "@/lib/payments.functions";
import { getStripeEnvironment } from "@/lib/stripe";

type CheckoutReturnSearch = { session_id?: string; return_to?: string };

const MAX_TRIES = 8;
const TICK_INTERVAL_MS = 1500;

export const Route = createFileRoute("/checkout/return")({
  validateSearch: (search: Record<string, unknown>): CheckoutReturnSearch => {
    const rt = typeof search.return_to === "string" ? search.return_to : undefined;
    const safeRt = rt && rt.startsWith("/") && !rt.startsWith("//") ? rt : undefined;
    return {
      session_id: typeof search.session_id === "string" ? search.session_id : undefined,
      return_to: safeRt,
    };
  },
  component: CheckoutReturn,
  head: () => ({ meta: [{ title: "Checkout complete — MissionReady" }] }),
});

function CheckoutReturn() {
  const { session_id, return_to: return_to_from_url } = Route.useSearch();
  const ent = useEntitlement();
  const navigate = useNavigate();
  const [confirmed, setConfirmed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const [pollNonce, setPollNonce] = useState(0);
  const retryCountRef = useRef(0);

  // Prefer the URL value (primary source of truth — piped through Stripe's
  // return_url). Fall back to sessionStorage, which billing.tsx stashes right
  // before opening checkout, so the auto-navigate still works if the query
  // param is stripped anywhere along the redirect chain.
  const return_to = useMemo(
    () => return_to_from_url ?? readCheckoutReturnTo(),
    [return_to_from_url],
  );

  // Poll-refresh entitlement for up to ~12s per attempt. `pollNonce` lets
  // "Retry" restart the loop with a fresh set of tries. Results land in
  // `ent` via state updates; a separate effect below reactively watches
  // `ent.status` to flip `confirmed` — avoiding the stale-closure trap
  // where the tick captured `ent.subscription: null` and never saw it fill.
  useEffect(() => {
    if (!session_id) return;
    let cancelled = false;
    let tries = 0;
    setAttempt(0);
    setTimedOut(false);

    // Server-side confirm: retrieve the Checkout Session on the server and
    // upsert the subscription row immediately. This removes the dependency
    // on the Stripe webhook arriving before we can show a confirmed state.
    // The realtime channel in useEntitlement will pick up the DB write and
    // flip `ent.status`; the reactive effect below then confirms the UI.
    // Fires alongside polling as a safety net.
    (async () => {
      try {
        await confirmCheckoutSession({
          data: { sessionId: session_id, environment: getStripeEnvironment() },
        });
        if (!cancelled) await ent.refresh();
      } catch (err) {
        console.warn("confirmCheckoutSession failed; falling back to polling", err);
      }
    })();

    const tick = async () => {
      if (cancelled) return;
      await ent.refresh();
      tries += 1;
      if (!cancelled) setAttempt(tries);
      if (tries < MAX_TRIES && !cancelled) {
        setTimeout(tick, TICK_INTERVAL_MS);
      } else if (!cancelled) {
        // Give the reactive effect one render to flip `confirmed`; if the
        // subscription really hasn't landed, show the timeout state.
        setTimeout(() => {
          if (!cancelled) setTimedOut(true);
        }, 250);
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session_id, pollNonce]);


  // Flip `confirmed` reactively once entitlement reflects an active plan.
  useEffect(() => {
    if (confirmed) return;
    if (ent.status === "trialing" || ent.status === "active") {
      setConfirmed(true);
      setTimedOut(false);
      trackAppEvent("checkout_return_confirmed", {
        session_id: session_id,
        return_to: return_to,
        status: ent.status,
        retries: retryCountRef.current,
      });
    }
  }, [ent.status, confirmed, session_id, return_to]);

  const waiting =
    !!session_id && !confirmed && !timedOut && !["trialing", "active"].includes(ent.status);

  // Once the subscription is confirmed and the user came from a deep-link
  // upgrade flow, send them back to where they were trying to do something.
  useEffect(() => {
    if (confirmed && return_to) {
      const t = setTimeout(() => {
        clearCheckoutReturnTo();
        navigate({ to: return_to as any });
      }, 800);
      return () => clearTimeout(t);
    }
  }, [confirmed, return_to, navigate]);

  const handleRetry = useCallback(() => {
    retryCountRef.current += 1;
    trackAppEvent("checkout_return_retry_clicked", {
      session_id: session_id,
      retries: retryCountRef.current,
    });
    setPollNonce((n) => n + 1);
  }, [session_id]);

  const progressValue = confirmed
    ? 100
    : Math.min(95, Math.round((attempt / MAX_TRIES) * 100));

  const secondsElapsed = Math.round((attempt * TICK_INTERVAL_MS) / 1000);

  return (
    <div className="container max-w-lg py-16">
      <Card>
        <CardContent className="text-center space-y-5 pt-8 pb-8">
          {confirmed ? (
            <CheckCircle2 className="h-12 w-12 text-success mx-auto" />
          ) : timedOut ? (
            <AlertTriangle className="h-12 w-12 text-warning mx-auto" aria-hidden />
          ) : waiting ? (
            <Loader2 className="h-12 w-12 text-primary mx-auto animate-spin" />
          ) : (
            <CheckCircle2 className="h-12 w-12 text-success mx-auto" />
          )}

          <div className="space-y-2">
            <h1 className="text-2xl font-bold">
              {confirmed
                ? "You're all set"
                : timedOut
                  ? "Still waiting on Stripe"
                  : waiting
                    ? "Finalising your subscription…"
                    : "You're all set"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {!session_id
                ? "We didn't receive a checkout session. If you completed payment, your subscription will appear in Billing shortly."
                : confirmed
                  ? return_to
                    ? "Your subscription is active. Taking you back to where you left off…"
                    : "Your 30-day free trial has started. Your card will be charged when the trial ends — cancel any time before then."
                  : timedOut
                    ? "Stripe hasn't confirmed the payment yet. This sometimes takes a minute during peak times. If you were charged, your subscription will show up in Billing shortly — you can retry the check or head back in the meantime."
                    : "We're confirming the payment with Stripe. This usually takes a second or two."}
            </p>
          </div>

          {waiting && (
            <div className="space-y-2 pt-1">
              <Progress
                value={progressValue}
                aria-label="Confirming subscription"
                className="h-1.5"
              />
              <p className="text-xs text-muted-foreground" aria-live="polite">
                Checking… attempt {attempt} of {MAX_TRIES}
                {secondsElapsed > 0 ? ` · ${secondsElapsed}s elapsed` : ""}
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-2 justify-center pt-2">
            {timedOut && (
              <Button onClick={handleRetry}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry check
              </Button>
            )}
            {return_to && (confirmed || timedOut) && (
              <Link to={return_to as any}>
                <Button variant={timedOut ? "outline" : "default"}>
                  Continue where you left off
                </Button>
              </Link>
            )}
            {(confirmed || timedOut || !session_id) && (
              <Link to="/billing">
                <Button variant={return_to || timedOut ? "outline" : "default"}>
                  Go to billing
                </Button>
              </Link>
            )}
            {(confirmed || timedOut || !session_id) && !return_to && (
              <Link to="/home">
                <Button variant="outline">Go to home</Button>
              </Link>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
